import { useEffect, useRef } from "react";
import {
  getVapidPublicKey,
  type PushSubscriptionData,
  unsubscribeFromPushNotifications as removeSubscriptionFromBackend,
  subscribeToPushNotifications as sendSubscriptionToBackend,
} from "../api/eventnotifier/push";
import {
  getPushSubscription,
  registerServiceWorker,
  requestNotificationPermission,
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
} from "../utils/serviceWorkerRegistration";
import { useApoAuth } from "./index";

interface UsePushNotificationsOptions {
  enabled?: boolean;
}

/**
 * Hook to manage push notification subscription
 * Works alongside SSE to provide browser push notifications
 */
export function usePushNotifications({ enabled = true }: UsePushNotificationsOptions = {}) {
  const { user, source_entity, isAuthenticated, getIdTokenClaims } = useApoAuth();
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);
  const subscriptionRef = useRef<PushSubscription | null>(null);
  const isSubscribingRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    if (!isAuthenticated) return;
    if (!user?.email || !source_entity) return;

    let mounted = true;

    const setupPushNotifications = async () => {
      // Prevent multiple simultaneous subscription attempts
      if (isSubscribingRef.current) return;
      isSubscribingRef.current = true;

      try {
        // Get auth token
        const tokenClaims = await getIdTokenClaims();
        if (!tokenClaims || !("__raw" in tokenClaims)) {
          console.error("[Push] Failed to get auth token");
          isSubscribingRef.current = false;
          return;
        }
        const token = (tokenClaims as { __raw: string }).__raw;

        // Register service worker
        const registration = await registerServiceWorker({
          onRegistered: (reg) => {
            registrationRef.current = reg;
          },
          onError: (error) => {
            console.error("[Push] Service worker registration error:", error);
          },
        });

        if (!registration || !mounted) {
          isSubscribingRef.current = false;
          return;
        }

        registrationRef.current = registration;

        // Request notification permission
        const permission = await requestNotificationPermission();
        if (permission !== "granted" || !mounted) {
          console.log("[Push] Notification permission not granted:", permission);
          isSubscribingRef.current = false;
          return;
        }

        // Get VAPID public key from backend
        const publicKey = await getVapidPublicKey();
        if (!publicKey || !mounted) {
          console.error("[Push] Failed to get VAPID public key");
          isSubscribingRef.current = false;
          return;
        }

        // Check if already subscribed
        let subscription = await getPushSubscription(registration);
        if (subscription && mounted && !!user.email && typeof user.email === "string") {
          subscriptionRef.current = subscription;
          // Send existing subscription to backend (in case backend was reset)
          const subscriptionData: PushSubscriptionData = {
            endpoint: subscription.endpoint,
            keys: {
              p256dh: arrayBufferToBase64(subscription.getKey("p256dh")),
              auth: arrayBufferToBase64(subscription.getKey("auth")),
            },
            expirationTime: subscription.expirationTime,
          };
          await sendSubscriptionToBackend(user.email, source_entity, subscriptionData, token);
          isSubscribingRef.current = false;
          return;
        }

        // Subscribe to push notifications
        subscription = await subscribeToPushNotifications(registration, publicKey);
        if (!subscription || !mounted) {
          // Subscription failed - this is expected in some scenarios (localhost, etc.)
          // Error already logged in subscribeToPushNotifications with appropriate level
          isSubscribingRef.current = false;
          return;
        }

        subscriptionRef.current = subscription;

        // Convert subscription to format expected by backend
        const subscriptionData: PushSubscriptionData = {
          endpoint: subscription.endpoint,
          keys: {
            p256dh: arrayBufferToBase64(subscription.getKey("p256dh")),
            auth: arrayBufferToBase64(subscription.getKey("auth")),
          },
          expirationTime: subscription.expirationTime,
        };

        if (!!user.email && typeof user.email === "string") {
          // Send subscription to backend
          const success = await sendSubscriptionToBackend(
            user.email,
            source_entity,
            subscriptionData,
            token,
          );

          if (success) {
            console.log("[Push] Successfully subscribed to push notifications");
          } else {
            console.error("[Push] Failed to send subscription to backend");
          }
        } else {
          console.error(
            "[Push] Failed to send subscription to backend: user.email is not a string",
          );
        }
      } catch (error) {
        // Only log unexpected errors as errors
        // Expected failures (AbortError, etc.) are already handled in subscribeToPushNotifications
        if (error instanceof DOMException && error.name === "AbortError") {
          // Already handled, don't log again
        } else {
          console.error("[Push] Error setting up push notifications:", error);
        }
      } finally {
        isSubscribingRef.current = false;
      }
    };

    setupPushNotifications();

    // Cleanup function
    return () => {
      mounted = false;
      const cleanup = async () => {
        if (registrationRef.current && subscriptionRef.current && user?.email && source_entity) {
          try {
            // Get auth token for cleanup
            const tokenClaims = await getIdTokenClaims();
            if (tokenClaims && "__raw" in tokenClaims) {
              const token = (tokenClaims as { __raw: string }).__raw;
              // Unsubscribe from browser
              await unsubscribeFromPushNotifications(registrationRef.current);
              // Remove from backend (only this device's subscription)
              await removeSubscriptionFromBackend(
                source_entity,
                user.email,
                token,
                subscriptionRef.current.endpoint,
              );
              console.log("[Push] Unsubscribed from push notifications");
            }
          } catch (error) {
            console.error("[Push] Error unsubscribing:", error);
          }
        }
      };
      cleanup();
    };
  }, [isAuthenticated, user?.email, source_entity, enabled, getIdTokenClaims]);

  return {
    isSubscribed: subscriptionRef.current !== null,
  };
}

/**
 * Helper function to convert ArrayBuffer to base64 URL-safe string
 */
function arrayBufferToBase64(buffer: ArrayBuffer | null): string {
  if (!buffer) return "";
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}
